import { useEffect, useRef } from 'react'

import { Badge } from '@/components/sui/badge'
import type { PlanType } from '@/lib/plans'
import { PLAN_DISPLAY_NAMES, PLAN_VISUAL_CONFIG } from '@/lib/plans'
import { cn } from '@/lib/utils'
import { useContextStore } from '@/stores/context'

import {
  trackFeaturePaywallBadgeClicked,
  trackFeaturePaywallBadgeViewed,
} from '../feature-paywall-dialog/feature-paywall-dialog.tracking'

export type { PlanType }

interface FeaturePaywallBadgeProps {
  minimumPlan: PlanType
  feature?: string
  className?: string
  onClick?: (event: React.MouseEvent) => void
}

export function FeaturePaywallBadge({
  minimumPlan,
  feature,
  className,
  onClick,
}: FeaturePaywallBadgeProps) {
  const currentPlan = useContextStore(
    (state) =>
      state.userAccountContext?.balanceInformation.featureType ?? 'Free',
  )

  const hasTrackedView = useRef(false)

  useEffect(() => {
    if (feature && !hasTrackedView.current) {
      trackFeaturePaywallBadgeViewed({ feature, minimumPlan, currentPlan })
      hasTrackedView.current = true
    }
  }, [feature, minimumPlan, currentPlan])

  const handleClick = (event: React.MouseEvent) => {
    if (feature) {
      trackFeaturePaywallBadgeClicked({
        feature,
        minimumPlan,
        currentPlan,
      })
    }
    onClick?.(event)
  }

  const visual = PLAN_VISUAL_CONFIG[minimumPlan]
  const PlanIcon = visual.icon

  return (
    <Badge
      variant={visual.badgeVariant}
      className={cn(
        'flex items-center gap-1 font-semibold',
        (feature || onClick) && 'cursor-pointer',
        className,
      )}
      onClick={feature || onClick ? handleClick : undefined}
    >
      <PlanIcon className="h-3 w-3" />
      <span>{PLAN_DISPLAY_NAMES[minimumPlan] ?? minimumPlan}</span>
    </Badge>
  )
}
